import type { Product } from '@/types/product'

// Simple Levenshtein distance for fuzzy matching
function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length
  const len2 = str2.length
  const matrix: number[][] = []

  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i]
  }

  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j
  }

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      )
    }
  }

  return matrix[len1][len2]
}

// Calculate similarity score (0-1, higher is better)
function similarity(str1: string, str2: string): number {
  const maxLen = Math.max(str1.length, str2.length)
  if (maxLen === 0) return 1
  const distance = levenshteinDistance(str1.toLowerCase(), str2.toLowerCase())
  return 1 - distance / maxLen
}

export interface ProductMatch {
  product: Product
  confidence: number
  matchedTerms: string[]
}

export interface ParsedItem {
  raw: string
  quantity: number
  item: string
}

// Parse shopping list text into items
export function parseShoppingList(text: string): ParsedItem[] {
  const lines = text
    .split(/\n|,|;/)
    .map(line => line.trim())
    .filter(line => line.length > 0)

  return lines.map(line => {
    // Extract quantity if present (e.g., "2 apples", "3x bananas", "5 kg rice")
    const quantityMatch = line.match(/^(\d+)\s*[xX×]?\s*(.+)$/) || 
                          line.match(/^(.+?)\s+(\d+)\s*(kg|g|liter|ml|pack|piece|pcs)?$/i)

    if (quantityMatch) {
      const qty = parseInt(quantityMatch[1], 10)
      const item = quantityMatch[2] || quantityMatch[1]
      return {
        raw: line,
        quantity: isNaN(qty) ? 1 : qty,
        item: item.trim()
      }
    }

    return {
      raw: line,
      quantity: 1,
      item: line
    }
  })
}

// Find matching products for a search term
export function findProductMatches(
  searchTerm: string,
  products: Product[],
  topN: number = 3
): ProductMatch[] {
  const normalizedSearch = searchTerm.toLowerCase()
  
  // Score each product
  const scored = products.map(product => {
    const title = product.title.toLowerCase()
    const category = product.category_name.toLowerCase()
    
    // Exact match bonus
    let score = 0
    if (title.includes(normalizedSearch)) {
      score += 0.8
    }
    
    // Category match bonus
    if (category.includes(normalizedSearch) || normalizedSearch.includes(category)) {
      score += 0.3
    }
    
    // Fuzzy match on title
    const titleWords = title.split(/\s+/)
    const searchWords = normalizedSearch.split(/\s+/)
    
    let bestWordMatch = 0
    for (const searchWord of searchWords) {
      for (const titleWord of titleWords) {
        const sim = similarity(searchWord, titleWord)
        if (sim > bestWordMatch) {
          bestWordMatch = sim
        }
      }
    }
    score += bestWordMatch * 0.7
    
    // Key features match
    const featuresText = (product.key_features || []).join(' ').toLowerCase()
    if (featuresText.includes(normalizedSearch)) {
      score += 0.2
    }
    
    return {
      product,
      confidence: Math.min(score, 1),
      matchedTerms: [searchTerm]
    }
  })

  // Filter low confidence and sort
  return scored
    .filter(match => match.confidence > 0.3)
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, topN)
}

// Match multiple items at once
export function matchShoppingList(
  items: ParsedItem[],
  products: Product[]
): Map<string, ProductMatch[]> {
  const matches = new Map<string, ProductMatch[]>()
  
  items.forEach(item => {
    const itemMatches = findProductMatches(item.item, products, 5)
    matches.set(item.raw, itemMatches)
  })
  
  return matches
}
